import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ExternalLink,
  IconBannedUser,
  IconCollection,
  IconSpinner,
  ResponsiveImage,
  UserBadgeList,
} from '../../../../components';
import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import { abbreviateName, formatCreatedAt } from '../../../../util/circleHelper';
import {
  fetchPostCommentList,
  toggleCreateComment,
  toggleReplyComment,
} from '../../CircleCommunityPage.duck';
import AddCommentContainer from './AddCommentContainer';
import CommentContainer, { removeTags } from './CommentContainer';
import css from './CircleFeedModal.module.css';
import { subscriptionPermissionRequest } from '../../../../ducks/user.duck';
import FeedInlineAttachments from '../FeedInlineAttachments';
import FeedDocumentAttachment from '../FeedDocumentAttachment/FeedDocumentAttachment';
import PollCard from '../Poll/PollCard';
import { RenderContent } from '../CircleFeedCard/CircleFeedCard';
import { FormattedMessage } from '../../../../util/reactIntl';
import FeedCoverPhoto from '../FeedCoverPhoto/FeedCoverPhoto';
import InReviewCard from '../../../../components/InReviewCard/InReviewCard';
import { createResourceLocatorString } from '../../../../util/routes';
import PostOptionsMenu from '../CircleFeedCard/PostOptionsMenu';
import { checkElephantModalOpen, getCommunityUserRole } from '../../../../util/data';

const FeedModalContainer = props => {
  const {
    feedData,
    handleCloseFeedModal = () => {},
    selectedPost,
    postCommentListData,
    currentCommunityMember,
    currentIndex,
    requestCreateComment,
    requestCreateReplyComment,
    onPostLikeDislike,
    checkCreatePostAuth,
    onDeleteBookMark,
    onCreateBookMark,
    requestDirectUpload,
    isRequestSpace,
    advancedSearchData,
    onAdvancedSearch,
    isReplyComment = false,
    loadMoreComments = false,
    requestPostCommentList = false,
    isAddComment = false,
    editCommentData = null,
    history,
    routeConfiguration,
    userModeratorsGroups,
    currentUser,
    onFetchComments,
    onSetElephantVerificationModal = () => {},
    userBadges = [],
    hasActiveSubscription = false,
    currentUserHasActiveSubscription = false,
    hasStripeConnected = false,
    currentUserHasStripeConnected = false,
    isBasicRole = false,
    pollActionInProgressById = {},
    pollActionErrorById = {},
    onSubmitPollVote,
    onClosePoll,
  } = props;
  const dispatch = useDispatch();
  const scrollSectionRef = useRef(null);
  const fixedBarRef = useRef(null);

  // On mobile: keep scroll section sized correctly above the fixed comment bar,
  // and shift the bar up when the keyboard opens (visualViewport shrinks).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => {
      const el = scrollSectionRef.current;
      const bar = fixedBarRef.current;

      // When on desktop (> 768px), clean up inline styles so desktop CSS rules take over
      if (window.innerWidth > 768) {
        if (el) {
          el.style.maxHeight = '';
          el.style.paddingBottom = '';
        }
        if (bar) {
          bar.style.bottom = '';
          bar.style.paddingBottom = '';
        }
        return;
      }

      if (!el) return;

      const vv = window.visualViewport;
      const visibleH = vv ? vv.height : window.innerHeight;
      // Keyboard offset from bottom of visual viewport
      const keyboardH = vv ? Math.max(window.innerHeight - vv.height - vv.offsetTop, 0) : 0;

      // Keep composer fixed at the very bottom of the screen, and lift it above keyboard when open
      if (bar) {
        if (keyboardH > 0) {
          bar.style.bottom = `${keyboardH}px`;
          bar.style.paddingBottom = '0px';
        } else {
          bar.style.bottom = '0px';
          bar.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
        }
      }

      // Use the composer's actual top edge so the scroll viewport never sits
      // underneath the fixed composer.
      const scrollTop = el.getBoundingClientRect().top;
      const fixedBarTop = bar ? bar.getBoundingClientRect().top : visibleH - keyboardH;
      const available = fixedBarTop - scrollTop - 6;
      el.style.maxHeight = Math.max(available, 80) + 'px';
      el.style.paddingBottom = '24px';
    };

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (resizeObserver) {
      if (fixedBarRef.current) resizeObserver.observe(fixedBarRef.current);
      if (scrollSectionRef.current) resizeObserver.observe(scrollSectionRef.current);
      if (scrollSectionRef.current && scrollSectionRef.current.parentElement) {
        resizeObserver.observe(scrollSectionRef.current.parentElement);
      }
    }

    // initial run after paint
    requestAnimationFrame(update);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      resizeObserver?.disconnect();
    };
  }, [isAddComment, isReplyComment, editCommentData]);
  const {
    id: postId,
    name,
    cover_image = '',
    display_title,
    is_comments_closed,
    is_comments_enabled,
    is_liking_enabled,
    comment_count,
    user_likes_count,
    is_liked,
    tiptap_body,
    space,
    author,
    body_plain_text,
    bookmark_id,
    created_at,
    flagged_for_approval_at = null,
    poll,
    pollHydrationError,
  } = feedData || {};
  const pollActionName = poll?.id ? pollActionInProgressById[poll.id] : null;

  // Check User Have permission for like comment
  const isNotPermission = checkElephantModalOpen(currentUser);

  // Validate that the current post matches the selected post
  // Use selectedPost.postId as the source of truth since records array can be re-sorted
  const isPostMismatch = selectedPost && selectedPost.postId && selectedPost.postId !== postId;

  const { content = [] } = tiptap_body?.body || {};
  const { id: spaceId, name: spaceName, slug: spaceSlug } = space || {};
  const { id: authorId, community_member_id, name: authorName, avatar_url, roles, headline } =
    author || {};
  const userRoleInGroup = getCommunityUserRole(userModeratorsGroups, spaceId);
  const { avatar_url: current_avatar_url, name: currentUserName } = currentCommunityMember || {};
  const currentVoterProfile = {
    name: currentUserName || '',
    avatarUrl: current_avatar_url || '',
  };

  const { page = 1, per_page = 10, has_next_page = false, count = 0, records = [] } =
    postCommentListData || {};

  const fetchComments = async () => {
    if (onFetchComments) {
      onFetchComments({ postId });
    } else {
      dispatch(fetchPostCommentList({ postId }));
    }
  };
  useEffect(() => {
    fetchComments();
  }, [currentIndex]);

  const handlePostLikeDislike = async () => {
    if (!checkCreatePostAuth && !isredirectToLogin) {
      dispatch(subscriptionPermissionRequest(true));
      return;
    }
    onPostLikeDislike(is_liked, postId, spaceId);
  };

  const handleSaveUnsave = () => {
    if (!checkCreatePostAuth) {
      dispatch(subscriptionPermissionRequest(true));
      return;
    }

    if (isNotPermission) {
      onSetElephantVerificationModal(true);
      return;
    }

    if (bookmark_id) {
      //delete bookmark

      onDeleteBookMark({ bookmarkId: bookmark_id, postId, spaceId });
    } else {
      //create bookmark
      const apiParams = {
        record_id: postId,
        bookmark_type: 'post', //for now we only have post type
        postId,
        spaceId,
      };

      onCreateBookMark(apiParams);
    }
  };

  const handleCommentSection = () => {
    if (isNotPermission) {
      handleCloseFeedModal();
      onSetElephantVerificationModal(true);
      return;
    }

    // Validate post/space match before allowing comment
    if (isPostMismatch) {
      console.warn('Post mismatch detected. Cannot add comment to different post.');
      return;
    }

    dispatch(toggleReplyComment());
    dispatch(toggleCreateComment(true));
  };

  const handleLoadMore = () => {
    const params = { page: page + 1, per_page, postId };
    if (onFetchComments) {
      onFetchComments(params);
    } else {
      dispatch(fetchPostCommentList(params));
    }
  };

  const isFirstLoad = useRef(true); // Ref to track the first load

  const hideSpacer = is_comments_enabled || is_liking_enabled;

  const handleSpaceClick = () => {
    return history.push(
      createResourceLocatorString(
        'JoinedGroupsCommunityPage',
        routeConfiguration,
        { id: spaceId, slug: spaceSlug },
        {}
      )
    );
  };

  const isOwnPost = headline && currentUser?.id?.uuid && headline == currentUser?.id?.uuid;
  const isPollPost = poll?.id;
  return (
    <div>
      <div className={css.topImage}>
        <FeedCoverPhoto coverPhoto={cover_image} />
      </div>
      <div className={css.paddingContainer}>
        {flagged_for_approval_at ? (
          <span className={css.reviewTextTop}>
            <InReviewCard />
          </span>
        ) : null}
        <div className={css.userWrapper}>
          <div className={css.imageUserDetails}>
            {!headline && authorName == 'Inactive User' ? (
              <IconBannedUser className={css.nameWrapper} />
            ) : (
              <ExternalLink href={`/u/${headline}`}>
                {avatar_url ? (
                  <ResponsiveImage alt={'FeedCoverPhoto'} imagewithURL={avatar_url} />
                ) : (
                  <div className={css.nameWrapper}>{abbreviateName(authorName)}</div>
                )}
                {hasStripeConnected ? (
                  <div className={css.verfiedIcon}>
                    <IconCollection name="profile_badge_stripe" />
                  </div>
                ) : hasActiveSubscription ? (
                  <div className={css.verfiedIcon}>
                    <IconCollection name="profile_badge_elephant" />
                  </div>
                ) : null}
              </ExternalLink>
            )}
            {/* <ExternalLink href={`/u/${headline}`}>
              {avatar_url ? (
                <ResponsiveImage imagewithURL={avatar_url} />
              ) : (
                <div className={css.nameWrapper}>{abbreviateName(authorName)}</div>
              )}
            </ExternalLink> */}
            <div className={css.authorDetailsContainer}>
              <div className={css.authorZone}>
                <span className={css.authorName}>{authorName}</span>

                <span className={css.timingZone}>{formatCreatedAt(created_at)}</span>
                <div className={css.postedText}>
                  <div className={css.postedSpaceText}>
                    Posted in <b onClick={handleSpaceClick}>{spaceName}</b>
                  </div>
                  <span className={css.timingZoneMobile}>{formatCreatedAt(created_at)}</span>
                </div>
              </div>

              {userBadges && userBadges.length > 0 && (
                <UserBadgeList badges={userBadges} maxDisplay={1} size="small" />
              )}
            </div>
          </div>
          <div className={css.shareWrapper}>
            {/* <div onClick={handleSaveUnsave}>
              {bookmark_id ? <ProfileIconCard type="unsave" /> : <ProfileIconCard type="save" />}
            </div> */}
            {/* <span className={css.shareText}>Share</span> */}

            {currentUser ? (
              <PostOptionsMenu
                feedData={feedData}
                currentCommunityMember={currentCommunityMember}
                handleSaveUnsave={handleSaveUnsave}
                userRoleInGroup={userRoleInGroup}
                isOwnPost={isOwnPost}
                currentUser={currentUser}
                onSetElephantVerificationModal={onSetElephantVerificationModal}
              />
            ) : null}
          </div>
        </div>

        <div
          ref={scrollSectionRef}
          className={css.scrollSection}
          onTouchMove={e => e.stopPropagation()}
        >
          {isPollPost ? null : <div className={css.titleText}>{name}</div>}
          <div className={css.descriptionText}>
            <RenderContent
              content={content}
              isShowMoreButtonVisible={false}
              sgids_to_object_map={tiptap_body?.sgids_to_object_map}
            />
          </div>
          <FeedInlineAttachments
            inline_attachments={tiptap_body?.inline_attachments}
            attachments={tiptap_body?.attachments}
          />
          {/* show documents */}
          <FeedDocumentAttachment
            inline_attachments={tiptap_body?.inline_attachments}
            attachments={tiptap_body?.attachments}
          />
          <PollCard
            poll={poll}
            hydrationError={pollHydrationError}
            voteInProgress={pollActionName === 'vote'}
            closeInProgress={pollActionName === 'close'}
            errorMessage={poll?.id ? pollActionErrorById[poll.id] : null}
            onVote={onSubmitPollVote}
            onClose={onClosePoll}
            currentVoterProfile={currentVoterProfile}
          />
          {hideSpacer ? <div className={css.spacer}></div> : null}
          <div className={css.functionWrapper}>
            <div className={css.likeSection}>
              {is_liking_enabled ? (
                <div onClick={handlePostLikeDislike}>
                  {is_liked ? <ProfileIconCard type="liked" /> : <ProfileIconCard type="like" />}
                </div>
              ) : null}
              {is_comments_enabled && !is_comments_closed ? (
                <div onClick={handleCommentSection}>
                  <ProfileIconCard type="comment" />
                </div>
              ) : null}
            </div>
            <div className={css.rightLikeSection}>
              {is_comments_enabled ? (
                <span>
                  {comment_count} {comment_count === 1 ? 'comment' : 'comments'}
                </span>
              ) : null}
              {is_liking_enabled ? (
                <span>
                  {user_likes_count} {user_likes_count === 1 ? 'like' : 'likes'}
                </span>
              ) : null}
            </div>
          </div>
          {/* comment section here */}
          {is_comments_closed && !!is_comments_enabled ? (
            <div className={css.commentLockedText}>
              <ProfileIconCard type="lock" />
              <span>comments are locked</span>
            </div>
          ) : null}
          {requestPostCommentList ? (
            <div className={css.centerSpinner}>
              {' '}
              <IconSpinner type={true} />{' '}
            </div>
          ) : (
            <div className={css.mobileScrollContainer}>
              {/* see comments */}
              {records
                ?.filter(comments => comments?.post_id == postId)
                .map(record => (
                  <CommentContainer
                    onSetElephantVerificationModal={onSetElephantVerificationModal}
                    key={record.id}
                    handleCloseFeedModal={handleCloseFeedModal}
                    commentData={record}
                    postId={postId}
                    spaceId={spaceId}
                    spaceName={spaceName}
                    spaceSlug={spaceSlug}
                    currentCommunityMember={currentCommunityMember}
                    requestCreateReplyComment={requestCreateReplyComment}
                    isRequestSpace={isRequestSpace}
                    is_comments_closed={is_comments_closed}
                    requestCreateComment={requestCreateComment}
                    requestDirectUpload={requestDirectUpload}
                    advancedSearchData={advancedSearchData}
                    onAdvancedSearch={onAdvancedSearch}
                    isFirstLoad={isFirstLoad}
                  />
                ))}
              {loadMoreComments ? (
                <div className={css.spinnerWrapper}>
                  <IconSpinner type="true" />
                </div>
              ) : has_next_page ? (
                <div className={css.showMoreButton} onClick={handleLoadMore}>
                  <FormattedMessage id="CircleCommunity.loadMoreComments" />
                </div>
              ) : null}
              {requestCreateComment ? (
                <div
                  className={css.requestCreateCommentButton}
                  id="requestCreateComment"
                  onClick={handleLoadMore}
                >
                  <IconSpinner type="true" />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      {/* add comment section */}
      <div ref={fixedBarRef} className={css.fixedBottomBar}>
        {(isReplyComment || (!isAddComment && !editCommentData)) &&
        checkCreatePostAuth &&
        is_comments_enabled &&
        !is_comments_closed &&
        !isPostMismatch ? (
          <div className={css.addCommentSection}>
            <div className={css.imageContainer}>
              <div className={css.imageWrapper}>
                {current_avatar_url ? (
                  <ResponsiveImage imagewithURL={current_avatar_url} />
                ) : (
                  <div className={css.nameWrapper}>{abbreviateName(currentUserName)}</div>
                )}
                {currentUserHasStripeConnected ? (
                  <div className={css.verfiedIcon}>
                    {' '}
                    <IconCollection name="profile_badge_stripe" />
                  </div>
                ) : currentUserHasActiveSubscription ? (
                  <div className={css.verfiedIcon}>
                    {' '}
                    <IconCollection name="profile_badge_elephant" />
                  </div>
                ) : null}
              </div>
            </div>
            <div className={css.addCommentContainer} onClick={handleCommentSection}>
              <FormattedMessage id="CircleCommunity.commentPlaceholder" />
            </div>
          </div>
        ) : null}
        <div className={css.replyContainerFixed}>
          {checkCreatePostAuth &&
          is_comments_enabled &&
          (editCommentData || (!isReplyComment && isAddComment)) &&
          !is_comments_closed &&
          !isPostMismatch ? (
            <AddCommentContainer
              currentCommunityMember={currentCommunityMember}
              postId={postId}
              spaceId={spaceId}
              spaceName={spaceName}
              spaceSlug={spaceSlug}
              requestCreateComment={requestCreateComment}
              requestDirectUpload={requestDirectUpload}
              advancedSearchData={advancedSearchData}
              onAdvancedSearch={onAdvancedSearch}
              currentUser={currentUser}
              onSetElephantVerificationModal={onSetElephantVerificationModal}
              isBasicRole={isBasicRole}
              editCommentData={editCommentData}
              // setIsSecondModalOpen={setIsSecondModalOpen}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FeedModalContainer;
